import { Check, ChevronsUpDown, Plus, Trash2 } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { SimpleSelect } from '@/components/ui/simple-select'
import {
  parseType,
  serializeType,
  type TypeExpression,
} from '@/lib/type-system'
import { cn } from '@/lib/utils'

// =============================================================================
// Types
// =============================================================================

export interface AvailableTemplate {
  slug: string
  name: string
}

interface TypeSelectorProps {
  value: string
  onChange: (type: string) => void
  availableTemplates: AvailableTemplate[]
  className?: string
}

// =============================================================================
// Constants
// =============================================================================

type CategoryValue =
  | 'integer'
  | 'string'
  | 'boolean'
  | 'float'
  | 'void'
  | 'list'
  | 'map'
  | 'tuple'
  | 'function'
  | 'template'

/** Options for the top-level dropdown, excluding templates (added dynamically) */
const BASE_OPTIONS: Array<{
  value: CategoryValue
  label: string
  group: string
}> = [
  { value: 'integer', label: 'integer', group: 'Primitives' },
  { value: 'string', label: 'string', group: 'Primitives' },
  { value: 'boolean', label: 'boolean', group: 'Primitives' },
  { value: 'float', label: 'float', group: 'Primitives' },
  { value: 'void', label: 'void (in-place)', group: 'Special' },
  { value: 'list', label: 'list (array)', group: 'Containers' },
  { value: 'map', label: 'map', group: 'Containers' },
  { value: 'tuple', label: 'tuple', group: 'Containers' },
  { value: 'function', label: 'function', group: 'Special' },
  { value: 'template', label: 'Template', group: 'Templates' },
]

// =============================================================================
// Helpers
// =============================================================================

/** Map a TypeExpression's top-level kind to the dropdown value */
function exprToDropdownValue(expr: TypeExpression): string {
  switch (expr.kind) {
    case 'base':
      return expr.name
    case 'special':
      return expr.name
    case 'template':
      return 'template'
    case 'list':
      return 'list'
    case 'map':
      return 'map'
    case 'tuple':
      return 'tuple'
  }
}

/** Create a default TypeExpression for a dropdown value */
function defaultExprForValue(value: string): TypeExpression {
  switch (value) {
    case 'integer':
    case 'string':
    case 'boolean':
    case 'float':
      return { kind: 'base', name: value }
    case 'function':
      return { kind: 'special', name: 'function' }
    case 'void':
      return { kind: 'special', name: 'void' }
    case 'list':
      return {
        kind: 'list',
        element: { kind: 'base', name: 'integer' },
      }
    case 'map':
      return {
        kind: 'map',
        key: { kind: 'base', name: 'string' },
        value: { kind: 'base', name: 'integer' },
      }
    case 'tuple':
      return {
        kind: 'tuple',
        elements: [
          { kind: 'base', name: 'string' },
          { kind: 'base', name: 'integer' },
        ],
      }
    case 'template':
      return { kind: 'template', templateSlug: '' }
    default:
      return { kind: 'base', name: 'integer' }
  }
}

function safeParse(value: string): TypeExpression | null {
  try {
    return parseType(value)
  } catch {
    return null
  }
}

/**
 * Identity for a tuple's element rows. The editor's value string is the only
 * source of truth for how many elements there are; `pendingRemoval` is the
 * removal the trash button asked the editor for, with the value the editor
 * will hold once it applies it. The splice lands when exactly that value
 * arrives — at once, or later if the editor applies changes asynchronously —
 * and the hint is kept while the value has not moved, because an unchanged
 * value cannot tell "not yet" from "declined". Any other length change
 * discards it, so a declined or superseded request never splices a value
 * other than the one it asked for. The next trash click replaces it.
 */
interface TupleSlots {
  ids: readonly string[]
  pendingRemoval: { index: number; expected: string } | null
}

const NO_TUPLE_SLOTS: TupleSlots = { ids: [], pendingRemoval: null }

/** Pad `ids` with fresh slot ids up to `length`, or truncate it down to `length`. */
function fitSlotIds(ids: readonly string[], length: number): readonly string[] {
  if (ids.length >= length) return ids.slice(0, length)
  return [
    ...ids,
    ...Array.from({ length: length - ids.length }, () => crypto.randomUUID()),
  ]
}

/** Fit `slots` to the tuple `value` of `length` elements; returns `slots` itself when nothing changes. */
function fitTupleSlots(
  slots: TupleSlots,
  value: string,
  length: number,
): TupleSlots {
  const { ids, pendingRemoval } = slots
  if (ids.length === length) return slots
  const nextIds =
    pendingRemoval !== null && pendingRemoval.expected === value
      ? ids.filter((_, idx) => idx !== pendingRemoval.index)
      : fitSlotIds(ids, length)
  return { ids: nextIds, pendingRemoval: null }
}

// =============================================================================
// Component
// =============================================================================

function TemplateCombobox({
  value,
  onChange,
  templates,
}: {
  value: string
  onChange: (slug: string) => void
  templates: AvailableTemplate[]
}) {
  const [open, setOpen] = useState(false)
  const selected = templates.find((t) => t.slug === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="min-w-28 justify-between text-sm"
        >
          <span className="truncate">
            {selected ? selected.name : 'Select template...'}
          </span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search templates..." className="h-8" />
          <CommandList>
            <CommandEmpty>No templates found.</CommandEmpty>
            {templates.map((t) => (
              <CommandItem
                key={t.slug}
                value={t.name}
                onSelect={() => {
                  onChange(t.slug)
                  setOpen(false)
                }}
              >
                <span>{t.name}</span>
                {value === t.slug && (
                  <Check className="ml-auto h-4 w-4 text-green-500" />
                )}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function TypeSelector({
  value,
  onChange,
  availableTemplates,
  className,
}: TypeSelectorProps) {
  const expr = useMemo(() => safeParse(value), [value])
  // Track when user selected "Template" but hasn't picked a specific one yet
  const [pendingTemplate, setPendingTemplate] = useState(false)

  // Tuple slots have no identity of their own: TypeExpression is a parsed
  // string, and each child TypeSelector holds local state (pendingTemplate)
  // that the string does not encode. Keyed by index, removing an element
  // would hand that state to the next element over, so every slot gets an
  // id that follows its element. The ids are fitted to the value during
  // render (React's pattern for state that depends on props): padded when
  // elements are appended, truncated when the value shrinks from outside,
  // and spliced at the removed index once the editor has applied a removal.
  const [tupleSlots, setTupleSlots] = useState<TupleSlots>(NO_TUPLE_SLOTS)
  const slots = fitTupleSlots(
    tupleSlots,
    value,
    expr?.kind === 'tuple' ? expr.elements.length : 0,
  )
  if (slots !== tupleSlots) {
    setTupleSlots(slots)
  }

  const dropdownOptions = useMemo(() => {
    return BASE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))
  }, [])

  const showTemplateCombobox = expr?.kind === 'template' || pendingTemplate
  const currentDropdownValue = showTemplateCombobox
    ? 'template'
    : expr
      ? exprToDropdownValue(expr)
      : value

  const handleTopLevelChange = useCallback(
    (newValue: string) => {
      if (newValue === 'template') {
        // Don't call onChange yet — wait for user to pick a template
        setPendingTemplate(true)
        return
      }
      setPendingTemplate(false)
      const newExpr = defaultExprForValue(newValue)
      onChange(serializeType(newExpr))
    },
    [onChange],
  )

  const handleChildChange = useCallback(
    (updatedExpr: TypeExpression) => {
      setPendingTemplate(false)
      onChange(serializeType(updatedExpr))
    },
    [onChange],
  )

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <SimpleSelect
        value={currentDropdownValue}
        onChange={handleTopLevelChange}
        options={dropdownOptions}
        width="auto"
        className="text-sm"
      />

      {/* List → child element type */}
      {expr?.kind === 'list' && (
        <div className="border-muted ml-3 flex items-center gap-1.5 border-l pl-3">
          <span className="text-muted-foreground text-xs">of</span>
          <TypeSelector
            value={serializeType(expr.element)}
            onChange={(childStr) => {
              const childExpr = safeParse(childStr)
              if (childExpr) {
                handleChildChange({ kind: 'list', element: childExpr })
              }
            }}
            availableTemplates={availableTemplates}
          />
        </div>
      )}

      {/* Map → key + value types */}
      {expr?.kind === 'map' && (
        <div className="border-muted ml-3 flex items-center gap-1.5 border-l pl-3">
          <span className="text-muted-foreground text-xs">key</span>
          <TypeSelector
            value={serializeType(expr.key)}
            onChange={(keyStr) => {
              const keyExpr = safeParse(keyStr)
              if (keyExpr) {
                handleChildChange({
                  kind: 'map',
                  key: keyExpr,
                  value: expr.value,
                })
              }
            }}
            availableTemplates={availableTemplates}
          />
          <span className="text-muted-foreground text-xs">value</span>
          <TypeSelector
            value={serializeType(expr.value)}
            onChange={(valStr) => {
              const valExpr = safeParse(valStr)
              if (valExpr) {
                handleChildChange({
                  kind: 'map',
                  key: expr.key,
                  value: valExpr,
                })
              }
            }}
            availableTemplates={availableTemplates}
          />
        </div>
      )}

      {/* Tuple → n element types with add/remove */}
      {expr?.kind === 'tuple' && (
        <div className="border-muted ml-3 space-y-1 border-l pl-3">
          {expr.elements.map((el, i) => (
            <div key={slots.ids[i]} className="flex items-center gap-1.5">
              <span className="text-muted-foreground text-xs min-w-6">
                [{i}]
              </span>
              <TypeSelector
                value={serializeType(el)}
                onChange={(elStr) => {
                  const elExpr = safeParse(elStr)
                  if (elExpr) {
                    const newElements = [...expr.elements]
                    newElements[i] = elExpr
                    handleChildChange({ kind: 'tuple', elements: newElements })
                  }
                }}
                availableTemplates={availableTemplates}
              />
              {expr.elements.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const next: TypeExpression = {
                      kind: 'tuple',
                      elements: expr.elements.filter((_, idx) => idx !== i),
                    }
                    setTupleSlots((s) => ({
                      ...s,
                      pendingRemoval: {
                        index: i,
                        expected: serializeType(next),
                      },
                    }))
                    handleChildChange(next)
                  }}
                  aria-label={`Remove element ${i}`}
                  className="text-destructive hover:bg-destructive/10 h-6 w-6 p-0"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              const newElements: TypeExpression[] = [
                ...expr.elements,
                { kind: 'base', name: 'integer' },
              ]
              handleChildChange({ kind: 'tuple', elements: newElements })
            }}
            className="h-6 gap-1 px-2 text-xs"
          >
            <Plus className="h-3 w-3" />
            Add element
          </Button>
        </div>
      )}

      {/* Template → searchable combobox */}
      {showTemplateCombobox && (
        <div className="border-muted ml-3 flex items-center gap-1.5 border-l pl-3">
          <TemplateCombobox
            value={expr?.kind === 'template' ? expr.templateSlug : ''}
            onChange={(slug) => {
              handleChildChange({ kind: 'template', templateSlug: slug })
            }}
            templates={availableTemplates}
          />
        </div>
      )}
    </div>
  )
}
